import { EiTableColumn, EiTableRow, EiTableSortState } from '../models/ei-table.model'

/**
 * Normalize text value for search and comparison.
 * Converts to lowercase string and trims whitespace.
 */
function normalizeText(value: unknown): string {
    if (value === null || value === undefined) return ''
    return String(value).trim().toLowerCase()
}

/**
 * Convert table row to record type for property access.
 */
function asRecord(row: EiTableRow): Record<string, unknown> {
    return row as unknown as Record<string, unknown>
}

/**
 * Get cell value from a row using column configuration.
 * Uses valueGetter if provided, otherwise accesses property directly.
 */
export function getCellValue(row: EiTableRow, column: EiTableColumn): unknown {
    if (column.valueGetter) return column.valueGetter(row)
    return asRecord(row)[column.key]
}

/**
 * Format cell value as displayable text string.
 */
export function formatCellText(row: EiTableRow, column: EiTableColumn): string {
    const cellValue = getCellValue(row, column)
    const isEmpty =
        cellValue === null ||
        cellValue === undefined ||
        (typeof cellValue === 'string' && cellValue.trim() === '') ||
        (Array.isArray(cellValue) && cellValue.length === 0) ||
        (typeof cellValue === 'object' &&
            !Array.isArray(cellValue) &&
            Object.keys(cellValue).length === 0)
    if (isEmpty) {
        return column.editable ? '' : '-'
    }
    return String(cellValue)
}

/**
 * Apply global search filter across all searchable columns.
 * Returns rows where at least one searchable column contains the search term.
 */
export function applyGlobalSearch(
    rows: EiTableRow[],
    columns: EiTableColumn[],
    searchTerm: string,
): EiTableRow[] {
    const normalizedSearchTerm = normalizeText(searchTerm)
    if (!normalizedSearchTerm) return rows

    const searchableColumns = columns.filter((column) => column.searchable)
    if (!searchableColumns.length) return rows

    return rows.filter((row) =>
        searchableColumns.some((column) =>
            normalizeText(getCellValue(row, column)).includes(normalizedSearchTerm),
        ),
    )
}

/**
 * Apply per-column search filters.
 * Returns rows where all active column searches match.
 */
export function applyColumnSearch(
    rows: EiTableRow[],
    columns: EiTableColumn[],
    columnSearch: Record<string, string>,
): EiTableRow[] {
    const activeColumnKeys = Object.keys(columnSearch).filter((key) =>
        normalizeText(columnSearch[key]),
    )
    if (!activeColumnKeys.length) return rows

    const columnMap = new Map(columns.map((column) => [column.key, column]))

    return rows.filter((row) =>
        activeColumnKeys.every((columnKey) => {
            const column = columnMap.get(columnKey)
            if (!column) return true
            const searchTerm = normalizeText(columnSearch[columnKey])
            return normalizeText(getCellValue(row, column)).includes(searchTerm)
        }),
    )
}

/**
 * Apply select/dropdown filters to rows.
 * Returns rows where all active filter values match cell values.
 */
export function applySelectFilters(
    rows: EiTableRow[],
    columns: EiTableColumn[],
    filters: Record<string, string | number | (string | number)[] | null>,
): EiTableRow[] {
    const activeFilterKeys = Object.keys(filters).filter((key) => {
        const filterValue = filters[key]
        if (filterValue === null || filterValue === '') return false
        if (Array.isArray(filterValue)) return filterValue.length > 0
        return true
    })
    if (!activeFilterKeys.length) return rows

    const columnMap = new Map(columns.map((column) => [column.key, column]))

    return rows.filter((row) =>
        activeFilterKeys.every((columnKey) => {
            const column = columnMap.get(columnKey)
            if (!column) return true
            const filterValue = filters[columnKey]
            const cellValue = getCellValue(row, column)

            if (Array.isArray(filterValue)) {
                return filterValue.some((value) => String(value) === String(cellValue))
            }

            return String(cellValue) === String(filterValue)
        }),
    )
}

/**
 * Apply sorting to rows based on sort state.
 * Returns a new sorted array without mutating the original.
 */
export function applySort(
    rows: EiTableRow[],
    columns: EiTableColumn[],
    sort: EiTableSortState,
): EiTableRow[] {
    if (!sort.activeKey || !sort.direction) return rows
    const sortColumn = columns.find((column) => column.key === sort.activeKey)
    if (!sortColumn) return rows

    const sortDirection = sort.direction === 'asc' ? 1 : -1
    const sortedRows = [...rows].sort((rowA, rowB) => {
        const valueA = normalizeText(getCellValue(rowA, sortColumn))
        const valueB = normalizeText(getCellValue(rowB, sortColumn))
        if (valueA < valueB) return -1 * sortDirection
        if (valueA > valueB) return 1 * sortDirection
        return 0
    })
    return sortedRows
}

/**
 * Paginate rows array based on page index and page size.
 * Returns the slice of rows for the current page.
 */
export function paginate(rows: EiTableRow[], pageIndex: number, pageSize: number): EiTableRow[] {
    if (pageSize <= 0) return rows
    const startIndex = pageIndex * pageSize
    return rows.slice(startIndex, startIndex + pageSize)
}
